import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  compose,
  setPropTypes,
  withState,
  lifecycle,
  withHandlers
} from 'recompose';
import { withRouter } from 'react-router';
import { withStyles } from '@material-ui/core/styles';
import Drawer from '@material-ui/core/Drawer';
import Paper from '@material-ui/core/Paper';
import moment from 'moment-timezone';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import MenuItem from '@material-ui/core/MenuItem';
import Header from '../../components/Header';
import MenuDrawer from '../../components/MenuDrawer';
import SearchButton from '../../components/atoms/SearchButton';
import GroupIdentityTable from '../organisms/GroupIdentityTable';
import CsvDownloadButton from '../../components/atoms/CsvDownloadButton';
import ProgressDialog from '../../components/molecules/ProgressDialog';
import { isAuthenticated, sendLogin } from '../../auth/Auth';
import { getGroupIdentities } from '../../actions/Identity';
import { getAccounts } from '../../actions/Accounts';
import { downloadGroup } from '../../actions/Groups';

const styles = {
  root: {
    backgroundColor: '#e0e0e0',
    flexGrow: 1,
    width: '100%'
  },
  pageTitileDiv: {
    paddingLeft: 20,
    paddingTop: 2,
    textAlign: 'left',
    backgroundColor: '#fff'
  },
  mainPaper: {
    position: 'relative',
    margin: 20
  },
  zoom: {
    position: 'relative',
    left: -10,
    top: 20
  },
  mainPaperTitleDiv: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '20px 26px 10px 26px'
  },
  searchForm: {
    padding: '10px 26px 10px 26px'
  },
  textField: {
    padding: '0px 5px 0px 5px',
    width: '300px'
  },
  textField150: {
    padding: '0px 5px 0px 5px',
    width: '200px'
  },
  button: {
    margin: '5px 5px'
  },
  buttonLeft: {
    textAlign: 'left'
    // CSV出力ボタンを実装時にコメントアウトを外す
    // float: 'left'
  },
  buttonRight: {
    textAlign: 'right'
  },
  buttonGroupLeft: {
    textAlign: 'left',
    float: 'left'
  },
  buttonGroupRight: {
    textAlign: 'right'
  }
};

const propTypes = {
  classes: PropTypes.object.isRequired
};

const enhance = compose(
  connect(
    state => ({
      account: state.accounts.account,
      statuses: state.identities.statuses
    }),
    dispatch => ({
      actions: bindActionCreators(
        { getGroupIdentities, getAccounts, downloadGroup },
        dispatch
      )
    })
  ),
  setPropTypes(propTypes),
  withState('openModal', 'updateOpenModal', false),
  withState('openEditModal', 'updateOpenEditModal', false),
  withState('openDetailModal', 'updateOpenDetailModal', false),
  withState('openDelete', 'updateOpenDelete', false),
  withState('openDrawer', 'updateOpenDrawer', false),
  withState('openTable', 'updateOpenTable', false),
  withState('openProgress', 'updateOpenProgress', false),
  withState('loading', 'updateLoading', false),
  withState('selected', 'setSelected', []),
  withState('page', 'setPage', 1),
  withState('rowsPerPage', 'setRowsPerPage', 100),
  withState('operatorName', 'setOperatorName', 0),
  withState('gochiType', 'setGochiType', 0),
  withState('status', 'setStatus', 'UNCONFIRMED'),
  withState('groupName', 'setGroupName', ''),
  withState('groupId', 'setGroupId', ''),
  withState('resultMessage', 'setResultMessage', ''),
  withHandlers({
    checkSession: props => data => {
      if (data.error === true && data.payload.status === 403) {
        sendLogin(props);
      }
      return data;
    },
    handleRedirect: props => path => {
      props.history.push(path);
    },
    handleChangePage: props => (event, page, limit) => {
      const table = document.getElementById('scrollTableId');
      if (table !== null) {
        table.scrollTop = 0;
      }
      props.setPage(page);

      const rowsPerPage = document.getElementById('rowsPerPage');
      let limitNum = 100;
      if (rowsPerPage) {
        limitNum = rowsPerPage.textContent;
      }

      const searchKey = {
        groupName: props.groupName,
        groupId: props.groupId,
        status: props.status,
        page,
        limit: limit || limitNum
      };

      props.updateLoading(true);
      props.updateOpenProgress(true);
      props.actions
        .getGroupIdentities(searchKey)
        .then(data => {
          if (data.error === true && data.payload.status === 403) {
            sendLogin(props);
          }
          props.updateOpenProgress(false);
          props.updateLoading(false);
          props.updateOpenTable(true);
        })
        .catch(() => {
          props.updateOpenProgress(false);
          props.updateLoading(false);
        });
    }
  }),
  lifecycle({
    componentDidMount() {
      isAuthenticated(this.props);
      this.props.actions.getAccounts({ cognitoUserId: '' }).then(async data => {
        this.props.checkSession(data);

        if (data.payload.role === 'GROUP') {
          this.props.actions
            .getGroupIdentities({
              page: this.props.page,
              limit: this.props.rowsPerPage
            })
            .then(identityData => this.props.checkSession(identityData));
          this.props.updateOpenTable(true);
        }
      });
    }
  })
);

const Main = enhance(
  ({
    actions,
    checkSession,
    classes,
    handleChangePage,
    account,
    statuses,
    openDrawer,
    updateOpenDrawer,
    openTable,
    updateOpenTable,
    loading,
    openProgress,
    groupName,
    setGroupName,
    groupId,
    setGroupId,
    page,
    status,
    setStatus,
    updateLoading,
    updateOpenProgress,
    setResultMessage,
    resultMessage
  }) => {
    const isShop = typeof account !== 'undefined' && account.role === 'SHOP';
    const isGroup = typeof account !== 'undefined' && account.role === 'GROUP';
    const isSecretariat =
      typeof account !== 'undefined' && account.role === 'SECRETARIAT';
    const isDeveloper =
      typeof account !== 'undefined' && account.role === 'DEVELOPER';
    const roles = {
      isShop,
      isGroup,
      isSecretariat,
      isDeveloper
    };

    const rowsPerPage = document.getElementById('rowsPerPage');
    let limitNum = 100;
    if (rowsPerPage) {
      limitNum = rowsPerPage.textContent;
    }

    const searchKey = {
      groupName,
      groupId,
      status,
      page,
      limit: limitNum
    };
    const searchAction = async () => {
      updateOpenTable(true);
      handleChangePage(null, 1, limitNum);

      // updateLoading(true);
      // updateOpenProgress(true);
      // await actions
      //   .getGroupIdentities(searchKey)
      //   .then(data => {
      //     checkSession(data);
      //     updateOpenProgress(false);
      //     updateLoading(false);
      //     handleChangePage(null, 0);
      //     updateOpenTable(true);
      //   })
      //   .catch(() => {
      //     updateOpenProgress(false);
      //     updateLoading(false);
      //   });
    };

    const downloadCsvBlob = (blob, fileName) => {
      if (window.navigator.msSaveOrOpenBlob) {
        // for IE,Edge
        window.navigator.msSaveOrOpenBlob(blob, fileName);
      } else {
        // for chrome, firefox
        const url = URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
        const linkEl = document.createElement('a');
        linkEl.href = url;
        linkEl.setAttribute('download', fileName);
        document.body.appendChild(linkEl);
        linkEl.click();
        linkEl.parentNode.removeChild(linkEl);
        // iOSのクリックイベント対策
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 1000);
      }
    };

    const csvDownloadAction = async () => {
      updateLoading(true);
      updateOpenProgress(true);
      const response = await actions
        .downloadGroup(searchKey)
        .then(data => checkSession(data));
      if (response.type === 'DOWNLOAD_GROUP_SUCCESS') {
        const timeStamp = moment()
          .tz('Asia/Tokyo')
          .format('YYYYMMDDHHmm');
        updateOpenProgress(false);
        setResultMessage('');
        updateLoading(false);
        downloadCsvBlob(response.payload, `${timeStamp}_group_info.csv`);
      } else {
        setResultMessage('店舗情報ダウンロードに失敗しました。');
        updateLoading(false);
      }
    };
    return (
      <div className={classes.root}>
        <Header
          onMenuClick={() => {
            updateOpenDrawer(true);
          }}
        />
        <Drawer
          open={openDrawer}
          anchor="left"
          onClose={() => {
            updateOpenDrawer(false);
          }}
        >
          <div tabIndex={0} role="button">
            <MenuDrawer account={account} />
          </div>
        </Drawer>
        <div className={classes.pageTitileDiv}>
          <Typography variant="h6" color="textSecondary">
            グループ本人確認書類検索
          </Typography>
        </div>
        <Paper className={classes.mainPaper} elevation={1}>
          <div className={classes.searchForm}>
            <div>
              {(roles.isSecretariat || roles.isDeveloper) && (
                <TextField
                  label="運営会社名"
                  placeholder="部分一致"
                  type="search"
                  className={classes.textField}
                  margin="normal"
                  value={groupName}
                  onChange={e => {
                    setGroupName(e.target.value);
                  }}
                />
              )}
              {(roles.isSecretariat || roles.isDeveloper) && (
                <TextField
                  label="グループID"
                  placeholder="部分一致"
                  type="search"
                  className={classes.textField}
                  margin="normal"
                  value={groupId}
                  onChange={e => {
                    setGroupId(e.target.value);
                  }}
                />
              )}
              {(roles.isSecretariat || roles.isDeveloper) && (
                <TextField
                  select
                  label="確認状況"
                  className={classes.textField}
                  margin="normal"
                  value={status}
                  onChange={e => {
                    setStatus(e.target.value);
                  }}
                  InputLabelProps={{
                    shrink: true
                  }}
                >
                  {statuses.map(item => {
                    return (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    );
                  })}
                </TextField>
              )}
            </div>
            {(roles.isSecretariat || roles.isDeveloper) && (
              <div>
                <div className={classes.buttonGroupLeft}>
                  <SearchButton onClickAction={searchAction} />
                </div>
                <div className={classes.buttonGroupRight}>
                  <CsvDownloadButton onClickAction={csvDownloadAction} />
                </div>
              </div>
            )}
          </div>
          {openTable && (
            <GroupIdentityTable
              searchKey={searchKey}
              roles={roles}
              page={page}
              handleChangePage={handleChangePage}
              selectedPage={page}
            />
          )}
        </Paper>
        <ProgressDialog
          openProgress={openProgress}
          loading={loading}
          resultMessage={resultMessage}
        />
      </div>
    );
  }
);

export default withRouter(withStyles(styles)(Main));
